import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaVolumeUp, FaVolumeMute, FaBook, FaSignInAlt, FaChevronLeft, FaChevronRight, FaTimes, FaImages, FaArrowRight, FaStar, FaMagic, FaFeather } from 'react-icons/fa';
import { StoryService } from '../services/StoryService';
import { StoryImageService, StoryImage } from '../services/StoryImageService';
import { StoryResponse } from '../types/story';
import '../styles/pages/SharedStory.css';
import { logger } from '../utils/logger';

/**
 * SharedStory page for viewing publicly shared stories
 */
const SharedStory: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [story, setStory] = useState<StoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [animationClass, setAnimationClass] = useState('');
  
  // Image-related state
  const [storyImages, setStoryImages] = useState<StoryImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const carouselIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [carouselActive, setCarouselActive] = useState(false);
  
  const navigate = useNavigate();

  // Fetch the shared story when component mounts
  useEffect(() => {
    const fetchSharedStory = async () => {
      if (!shareCode) {
        setError('No share code provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const sharedStory = await StoryService.getSharedStory(shareCode);
        setStory(sharedStory);
        
        // Preload audio if available
        if (sharedStory.audioUrl) {
          const audio = new Audio(sharedStory.audioUrl);
          audio.addEventListener('ended', () => setIsPlaying(false));
          audio.addEventListener('pause', () => setIsPlaying(false));
          audio.addEventListener('play', () => setIsPlaying(true));
          setAudioElement(audio);
        }

        // If the story has images, fetch them
        if (sharedStory.id && sharedStory.has_images) {
          await fetchStoryImages(sharedStory.id);
        }

        // Add animation class after a small delay for a nice entrance effect
        setTimeout(() => {
          setAnimationClass('content-visible');
        }, 300);
      } catch (err) {
        logger.error('Error fetching shared story:', err);
        setError(err instanceof Error ? err.message : 'Failed to load shared story');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedStory();
    
    // Clean up on unmount
    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [shareCode]);

  // Fetch images for the story
  const fetchStoryImages = async (storyId: string) => {
    try {
      setIsLoadingImages(true);
      const images = await StoryImageService.getStoryImages(storyId);
      if (images && images.length > 0) {
        setStoryImages(images);
        logger.log('Fetched story images:', images.length);
      }
    } catch (err) {
      logger.error('Error fetching story images:', err);
    } finally {
      setIsLoadingImages(false);
    }
  };

  // Toggle audio playback
  const togglePlayback = () => {
    if (!audioElement) return;
    
    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
  };

  // Start or stop the image carousel
  const toggleCarousel = () => {
    if (carouselActive) {
      // Stop carousel
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
        carouselIntervalRef.current = null;
      }
      setCarouselActive(false);
    } else {
      // Start carousel
      const interval = setInterval(() => {
        navigateImage('next');
      }, 3000);
      carouselIntervalRef.current = interval;
      setCarouselActive(true);
    }
  };

  // Function to open modal with specific image
  const openModal = (index: number) => {
    setActiveImageIndex(index);
    setModalOpen(true);
    
    // Pause audio when opening modal if it's playing
    if (isPlaying && audioElement) {
      audioElement.pause();
    }
  };
  
  // Function to close modal
  const closeModal = () => {
    setModalOpen(false);
    setCarouselActive(false);
    // Clear any carousel intervals
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
      carouselIntervalRef.current = null;
    }
  };
  
  // Navigate to previous or next image
  const navigateImage = (direction: 'prev' | 'next') => {
    if (storyImages.length <= 1) return;
    
    setActiveImageIndex(prevIndex => {
      if (direction === 'prev') {
        return prevIndex === 0 ? storyImages.length - 1 : prevIndex - 1;
      } else {
        return prevIndex === storyImages.length - 1 ? 0 : prevIndex + 1;
      }
    });
  };

  // Render image gallery
  const renderImageGallery = () => {
    if (isLoadingImages) {
      return (
        <div className="story-images-section">
          <h3>Story Illustrations</h3>
          <div className="image-loading">
            <div className="loading-spinner small"></div>
            <p>Loading magical illustrations...</p>
          </div>
        </div>
      );
    }
    
    if (storyImages.length > 0) {
      return (
        <div className="story-images-section">
          <h3>Story Illustrations</h3>
          <div className="gallery-intro">
            <p>Images created especially for this story</p>
            <button className="view-all-button" onClick={() => openModal(0)}>
              View All <FaImages />
            </button>
          </div>
          
          <div className="image-gallery">
            {storyImages.map((image, index) => (
              <div 
                key={image.id} 
                className="story-image-container"
                onClick={() => openModal(index)}
              >
                <img 
                  src={image.image_path} 
                  alt={image.caption || `Story illustration ${index + 1}`}
                  className="story-image"
                />
                <div className="image-overlay">
                  <span className="view-larger">View Larger</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Add the image modal component
  const renderImageModal = () => {
    if (!modalOpen || storyImages.length === 0) return null;
    
    const currentImage = storyImages[activeImageIndex];
    
    return (
      <div className="image-modal-overlay" onClick={closeModal}>
        <div className="image-modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close-button" onClick={closeModal}>
            <FaTimes />
          </button>
          
          <div className="modal-image-container">
            {storyImages.length > 1 && (
              <button 
                className="modal-nav-button prev"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('prev');
                }}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>
            )}
            
            <img 
              src={currentImage.image_path} 
              alt={currentImage.caption || `Story illustration ${activeImageIndex + 1}`} 
              className="modal-image"
            />
            
            {storyImages.length > 1 && (
              <button 
                className="modal-nav-button next"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('next');
                }}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
          
          <div className="modal-caption">
            <p>{currentImage.caption || `Scene ${activeImageIndex + 1}`}</p>
            <div className="modal-controls">
              {storyImages.length > 1 && (
                <button 
                  className={`carousel-toggle ${carouselActive ? 'active' : ''}`}
                  onClick={toggleCarousel}
                >
                  {carouselActive ? 'Pause Slideshow' : 'Start Slideshow'}
                </button>
              )}
              <div className="modal-pagination">
                {activeImageIndex + 1} / {storyImages.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render method
  return (
    <div className="shared-story-container">
      {loading ? (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-message">Loading Story</div>
          <div className="loading-subtext">Please wait while we prepare your story...</div>
        </div>
      ) : error ? (
        <div className="content-container">
          <div className="error-container">
            <div className="error-icon">😕</div>
            <h2>Story Not Found</h2>
            <p className="error-message">{error}</p>
            <button className="primary-button" onClick={() => navigate('/')}>
              Return Home
            </button>
          </div>
        </div>
      ) : story && (
        <div className={`story-wrapper ${animationClass}`}>
          <div className="content-container">
            {/* Story Invitation CTA with sign-up button */}
            <div className="story-invitation">
              <div className="story-invitation-content">
                <h3>Your Magical Journey Awaits <span className="sparkle">✨</span></h3>
                <p>
                  Settle in for a special adventure crafted just for your little dreamer. 
                  This tale will transport you to a world of imagination and wonder.
                </p>
                <button 
                  className="invitation-cta-button"
                  onClick={() => navigate('/signup')}
                >
                  Create Your Own Stories <FaMagic />
                </button>
              </div>
            </div>

            <div className="story-content">
              <h2 className="story-content-title">{story.title}</h2>
              
              {story.audioUrl && (
                <div className="audio-controls">
                  <button 
                    className="audio-button"
                    onClick={togglePlayback}
                    aria-label={isPlaying ? 'Pause audio narration' : 'Play audio narration'}
                  >
                    {isPlaying ? <FaVolumeMute /> : <FaVolumeUp />}
                    {isPlaying ? 'Pause Narration' : 'Listen to Narration'}
                  </button>
                </div>
              )}
              
              <div className="story-text">
                {story.text.split('\n\n').map((paragraph: string, index: number) => (
                  <p key={index} className="story-paragraph">{paragraph}</p>
                ))}
              </div>
            </div>
            
            {storyImages.length > 0 && (
              <div className="story-images-section">
                <h3>Story Illustrations</h3>
                <div className="gallery-intro">
                  <p>Images created especially for this story</p>
                  <button className="view-all-button" onClick={() => openModal(0)}>
                    View All <FaImages />
                  </button>
                </div>
                <div className="image-gallery">
                  {storyImages.map((image, index) => (
                    <div 
                      key={image.id} 
                      className="story-image-container"
                      onClick={() => openModal(index)}
                    >
                      <img 
                        src={image.image_path} 
                        alt={image.caption || `Story illustration ${index + 1}`}
                        className="story-image"
                      />
                      <div className="image-overlay">
                        <span className="view-larger">View Larger</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {renderImageModal()}
        </div>
      )}
    </div>
  );
};

export default SharedStory; 